import { useState, useEffect } from 'react';
import { User, Hand } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { useLanguage } from '@/contexts/LanguageContext';

interface HeroData {
  tagline: {
    ar: string;
    en: string;
  };
  title: {
    ar: string;
    en: string;
  };
  titleHighlight: {
    ar: string;
    en: string;
  };
  description: {
    ar: string;
    en: string;
  };
  backgroundImage: string;
  videoUrl: string;
  videoTitle: {
    ar: string;
    en: string;
  };
  primaryButton: {
    text: {
      ar: string;
      en: string;
    };
    link: string;
  };
  secondaryButton: {
    text: {
      ar: string;
      en: string;
    };
    link: string;
  };
}

export function LandingHero() {
  const [heroData, setHeroData] = useState<HeroData | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchHeroData();
  }, []);

  const fetchHeroData = async () => {
    try {
      const docRef = doc(db, 'landing_hero', 'main');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setHeroData(docSnap.data() as HeroData);
      }
    } catch (err) {
      console.error('Error fetching hero data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="relative pt-20 pb-12 min-h-[500px] flex items-center justify-center">
        <div className="text-center">{t('landing.common.loading')}</div>
      </section>
    );
  }

  if (!heroData) {
    return null;
  }

  // Get content based on current language
  const tagline = heroData.tagline[language] || heroData.tagline.ar;
  const title = heroData.title[language] || heroData.title.ar;
  const titleHighlight = heroData.titleHighlight[language] || heroData.titleHighlight.ar;
  const description = heroData.description[language] || heroData.description.ar;
  const videoTitle = heroData.videoTitle[language] || heroData.videoTitle.ar;
  const primaryButtonText = heroData.primaryButton.text[language] || heroData.primaryButton.text.ar;
  const secondaryButtonText = heroData.secondaryButton.text[language] || heroData.secondaryButton.text.ar;

  // Split title by the highlight word
  const titleParts = title.split(titleHighlight);

  return (
    <section className="relative pt-20 pb-12">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url("${heroData.backgroundImage}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#004250]/95 via-[#006778]/90 to-[#7dda55]/85"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="text-white">
            <div className="inline-block bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-white/20">
              <p className="text-lg font-medium text-white">
                {tagline}
              </p>
            </div>
            <h1 className="text-[3.4235rem] md:text-[4.10871rem] font-bold mb-6 leading-tight">
              <span className="text-white">{titleParts[0]}</span>
              <span className="text-[#7dda55]">{titleHighlight}</span>
              <span className="text-white">{titleParts[1]}</span>
            </h1>
            <p className="text-[1.5125rem] mb-8 text-white/90">
              {description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={heroData.primaryButton.link}
                className="bg-[#7dda55] text-white px-6 sm:px-8 py-3 rounded-full font-bold hover:bg-white hover:text-[#004250] transition duration-300 shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2 text-center text-base sm:text-lg"
              >
                <User className="h-5 w-5" />
                {primaryButtonText}
              </a>
              <a 
                href={heroData.secondaryButton.link}
                className="bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 rounded-full font-bold hover:bg-white hover:text-[#004250] transition duration-300 border border-white/20 hover:border-white inline-flex items-center justify-center gap-2 text-center text-base sm:text-lg"
              >
                <Hand className="h-5 w-5" />
                {secondaryButtonText}
              </a>
            </div>
          </div>

          {/* Video */}
          <div className="relative">
            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl">
              <iframe
                src={heroData.videoUrl}
                title={videoTitle}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>

      {/* Decorative gradient at bottom */}
      <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
}
